import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type { AnimalHistorico } from '../types/interfaces';
import { DEFAULT_ACTIVE_FIELDS } from '../configs/activeFieldsConfig';
import { generateSessionCode } from '../helpers/sessionCode';
import { CARGA_SESSION_STORAGE_KEYS } from '../helpers/cargaSessionKeys';
import { normalizeCaravana } from '../utils/animales';
import { calcularPesoPromedioPorSexo, calcularConteoPorSexo } from '../utils/sexoStats';
import { AuthContext } from './AuthContext';

/**
 * Claves para persistencia de sesión en localStorage
 * (compartidas con mobile vía helpers/cargaSessionKeys)
 */
const SESSION_STORAGE_KEYS = CARGA_SESSION_STORAGE_KEYS;

/**
 * Guarda el estado de la sesión en localStorage
 */
function saveSessionState(key: string, value: string | null | Set<string>): void {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else if (value instanceof Set) {
      localStorage.setItem(key, JSON.stringify([...value]));
    } else {
      localStorage.setItem(key, value);
    }
  } catch (error) {
    console.error(`[SessionContext] Error saving ${key} to localStorage:`, error);
  }
}

/**
 * Carga el estado de la sesión desde localStorage
 */
function loadSessionState<T>(key: string, parser?: (value: string) => T): T | null {
  try {
    const stored = localStorage.getItem(key);
    if (stored === null) return null;
    return parser ? parser(stored) : stored as T;
  } catch (error) {
    console.error(`[SessionContext] Error loading ${key} from localStorage:`, error);
    return null;
  }
}

/**
 * Limpia todos los datos de sesión del localStorage
 */
function clearSessionState(): void {
  try {
    Object.values(SESSION_STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    console.log('[SessionContext] Session state cleared from localStorage');
  } catch (error) {
    console.error('[SessionContext] Error clearing session state:', error);
  }
}

/**
 * Estadísticas de la sesión de carga
 */
interface SessionStats {
  /** Cantidad de animales en la sesión */
  cantidad_animales: number;
  /** Suma total de pesos */
  peso_total: number;
  /** Peso promedio (solo animales con peso) */
  peso_promedio: number;
  /** Peso promedio de machos */
  peso_promedio_macho: number;
  /** Peso promedio de hembras */
  peso_promedio_hembra: number;
  /** Peso promedio de animales sin sexo definido */
  peso_promedio_sin_sexo: number;
  /** Cantidad de machos */
  cantidad_macho: number;
  /** Cantidad de hembras */
  cantidad_hembra: number;
  /** Cantidad de animales sin sexo definido */
  cantidad_sin_sexo: number;
}

/**
 * Contexto para gestionar sesiones de carga de animales.
 *
 * Una sesión de carga agrupa todos los animales que se cargan
 * durante una misma sesión de trabajo, hasta que el usuario sale.
 *
 * Formato del session_code: YYYY-MM-DD-timestamp
 * Ejemplo: 2026-03-26-1743023456789
 */

interface SessionContextType {
  /** Código de sesión actual, o null si no hay sesión activa */
  sessionCode: string | null;
  /** Estadísticas de la sesión actual */
  sessionStats: SessionStats;
  /** Animales de la sesión actual (en memoria) */
  sessionAnimals: AnimalHistorico[];
  /** Última raza seleccionada en la sesión (caché en memoria) */
  lastRaza: string | null;
  /** Último Renspa seleccionado en la sesión (caché en memoria) */
  lastRenspa: string | null;
  /** Última categoría seleccionada en la sesión (caché en memoria) */
  lastCategoria: string | null;
  /** Última ubicación seleccionada en la sesión (caché en memoria, {id, nombre}) */
  lastUbicacion: { id: string; nombre: string } | null;
  /** Inicia una nueva sesión y retorna el código generado */
  startNewSession: () => string;
  /** Agrega un animal a la sesión y actualiza estadísticas */
  addAnimalToSession: (animal: AnimalHistorico) => void;
  /** Saca un animal de la sesión (registro anulado: eliminarDeSesion) */
  removeAnimalFromSession: (caravana: string) => void;
  /** Renombra la caravana de un animal de la sesión (corregirCaravana) */
  renameAnimalInSession: (caravanaVieja: string, caravanaNueva: string) => void;
  /**
   * Actualiza campos de un animal del caché de sesión (p.ej. tras la edición
   * inline del SessionAnimalsEditor, que escribe el histórico directo en
   * Supabase). Merge directo: las keys pasadas pisan siempre, incluso con
   * undefined (clear explícito del select "-").
   */
  updateAnimalInSession: (caravana: string, campos: Partial<AnimalHistorico>) => void;
  /** Establece la última raza seleccionada */
  setLastRaza: (raza: string | null) => void;
  /** Establece el último Renspa seleccionado */
  setLastRenspa: (renspa: string | null) => void;
  /** Establece la última categoría seleccionada */
  setLastCategoria: (categoria: string | null) => void;
  /** Establece la última ubicación seleccionada */
  setLastUbicacion: (ubicacion: { id: string; nombre: string } | null) => void;
  /** Limpia los animales de la sesión (pero mantiene el código) */
  clearSessionAnimals: () => void;
  /** Limpia la sesión actual (código y animales) */
  clearSession: () => void;
  /** Campos activos para el formulario de carga */
  activeFields: Set<string>;
  /** Establece los campos activos */
  setActiveFields: (fields: Set<string>) => void;
  /** true si hay una sesión activa */
  isActive: boolean;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

/**
 * Agrega o actualiza un animal en la lista de la sesión (por caravana).
 * Actualiza si el fecha_de_evento es más reciente; con fechas iguales gana
 * el último agregado (fecha_de_creacion). Compartido por addAnimalToSession
 * y renameAnimalInSession.
 *
 * Al actualizar, MERGEA sobre el registro existente en vez de reemplazarlo:
 * los payloads del form son parciales (solo campos tocados por el usuario),
 * y el reemplazo perdía del caché campos guardados en ediciones previas
 * (ej. el sexo volvía a "-" en el listado en vivo).
 */
function upsertAnimal(prev: AnimalHistorico[], animal: AnimalHistorico): AnimalHistorico[] {
  const existingIndex = prev.findIndex(a => a.caravana === animal.caravana);

  if (existingIndex === -1) {
    return [...prev, animal];
  }

  const existing = prev[existingIndex];
  const existingFechaEvento = existing.fecha_de_evento || existing.fecha_de_creacion || '';
  const newFechaEvento = animal.fecha_de_evento || animal.fecha_de_creacion || '';

  if (newFechaEvento < existingFechaEvento) {
    // El existente es más reciente, mantenerlo
    return prev;
  }
  // Más reciente o fechas iguales: el nuevo se mergea sobre el existente.
  // Keys undefined/null del nuevo no pisan (los payloads parciales traen
  // madre: undefined, etc.); el form ya excluye valores vacíos, así que no
  // hay caso de "clear" que este merge tape.
  const merged: AnimalHistorico = { ...existing };
  for (const [key, value] of Object.entries(animal)) {
    if (value !== undefined && value !== null) {
      (merged as any)[key] = value;
    }
  }
  const updated = [...prev];
  updated[existingIndex] = merged;
  return updated;
}

interface SessionProviderProps {
  children: React.ReactNode;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({ children }) => {
  // Inicializar estado desde localStorage si existe
  const [sessionCode, setSessionCode] = useState<string | null>(() => {
    return loadSessionState<string>(SESSION_STORAGE_KEYS.SESSION_CODE);
  });
  const [sessionAnimals, setSessionAnimals] = useState<AnimalHistorico[]>(() => {
    const stored = loadSessionState<unknown>(SESSION_STORAGE_KEYS.SESSION_ANIMALS, (v) => JSON.parse(v));
    if (Array.isArray(stored)) {
      return stored as AnimalHistorico[];
    }
    return [];
  });
  const [lastRaza, setLastRazaState] = useState<string | null>(() => {
    return loadSessionState<string>(SESSION_STORAGE_KEYS.LAST_RAZA);
  });
  const [lastRenspa, setLastRenspaState] = useState<string | null>(() => {
    return loadSessionState<string>(SESSION_STORAGE_KEYS.LAST_RENSPA);
  });
  const [lastCategoria, setLastCategoriaState] = useState<string | null>(() => {
    return loadSessionState<string>(SESSION_STORAGE_KEYS.LAST_CATEGORIA);
  });
  const [lastUbicacion, setLastUbicacionState] = useState<{ id: string; nombre: string } | null>(() => {
    const stored = loadSessionState<unknown>(SESSION_STORAGE_KEYS.LAST_UBICACION, (v) => JSON.parse(v));
    if (stored && typeof stored === 'object' && 'id' in (stored as object)) {
      return stored as { id: string; nombre: string };
    }
    return null;
  });
  const [activeFields, setActiveFieldsState] = useState<Set<string>>(() => {
    const stored = loadSessionState<string[]>(SESSION_STORAGE_KEYS.ACTIVE_FIELDS, (v) => JSON.parse(v));
    return stored ? new Set(stored) : DEFAULT_ACTIVE_FIELDS;
  });

  // Flag para evitar guardar estado inicial al montar
  const isInitializedRef = useRef(false);

  // Empresa activa (AuthContext). Acceso con useContext crudo para que el
  // provider siga funcionando standalone (tests, árboles sin AuthProvider).
  const auth = useContext(AuthContext);
  const activeEmpresaId = auth?.activeEmpresaId ?? null;

  /**
   * Empresa activa del último render (para persistirla junto al sessionCode
   * sin re-disparar el effect de guardado cuando cambia la empresa).
   */
  const activeEmpresaIdRef = useRef<string | null>(activeEmpresaId);
  activeEmpresaIdRef.current = activeEmpresaId;

  /**
   * Última empresa activa resuelta observada por el effect de aislamiento.
   * undefined = todavía no se resolvió (arranque de la app).
   */
  const lastEmpresaRef = useRef<string | null | undefined>(undefined);

  /**
   * Referencia tardía a clearSession (definido más abajo) para usarlo en el
   * effect de aislamiento sin depender de su identidad en los deps.
   */
  const clearSessionRef = useRef<() => void>(() => {});

  /**
   * Efecto: Guardar sessionCode en localStorage cuando cambie (después de inicialización).
   * Junto al código se persiste la empresa activa, para poder descartar en el
   * próximo arranque una sesión restaurada que pertenezca a otra empresa.
   */
  useEffect(() => {
    if (isInitializedRef.current) {
      saveSessionState(SESSION_STORAGE_KEYS.SESSION_CODE, sessionCode);
      saveSessionState(SESSION_STORAGE_KEYS.EMPRESA_ID, sessionCode ? activeEmpresaIdRef.current : null);
    }
  }, [sessionCode]);

  /**
   * Efecto: aislar la sesión de carga por empresa.
   * - Al cambiar la empresa activa (switchCompany o cambio de usuario) se
   *   descarta la sesión en curso para no arrastrar animales de la otra empresa.
   * - Al resolverse la empresa por primera vez (arranque / login), la sesión
   *   restaurada se valida contra la empresa persistida en localStorage.
   */
  useEffect(() => {
    const prev = lastEmpresaRef.current;
    const current = activeEmpresaId;
    if (prev === current) return;
    lastEmpresaRef.current = current;

    if (!sessionCode) return;

    if (prev === undefined || prev === null) {
      // Empresa recién resuelta: validar la sesión restaurada contra la persistida
      const persistedEmpresa = loadSessionState<string>(SESSION_STORAGE_KEYS.EMPRESA_ID);
      if (persistedEmpresa && current && persistedEmpresa !== current) {
        console.log(`[SessionContext] La sesión restaurada pertenece a otra empresa (${persistedEmpresa}); se descarta`);
        clearSessionRef.current();
      } else if (!persistedEmpresa && current) {
        // Sesión legacy (cargada antes de tagear la empresa): asumirla a la empresa activa
        saveSessionState(SESSION_STORAGE_KEYS.EMPRESA_ID, current);
      }
    } else {
      // Había una empresa activa distinta (switch o cambio de usuario): descartar siempre
      console.log(`[SessionContext] Cambio de empresa (${prev} -> ${current}); se limpia la sesión de carga`);
      clearSessionRef.current();
    }
  }, [activeEmpresaId, sessionCode]);

  /**
   * Efecto: Guardar activeFields en localStorage cuando cambie (después de inicialización)
   */
  useEffect(() => {
    if (isInitializedRef.current) {
      saveSessionState(SESSION_STORAGE_KEYS.ACTIVE_FIELDS, activeFields);
    }
  }, [activeFields]);

  /**
   * Efecto: Guardar sessionAnimals en localStorage cuando cambie (después de inicialización)
   */
  useEffect(() => {
    if (isInitializedRef.current) {
      console.log('[SessionContext] Guardando sessionAnimals en localStorage:', sessionAnimals.length);
      saveSessionState(SESSION_STORAGE_KEYS.SESSION_ANIMALS, JSON.stringify(sessionAnimals));
    }
  }, [sessionAnimals]);

  /**
   * Efecto: Marcar como inicializado después del primer render
   */
  useEffect(() => {
    isInitializedRef.current = true;
    if (sessionCode) {
      console.log('[SessionContext] Sesión restaurada desde localStorage:', sessionCode);
    }
    if (sessionAnimals.length > 0) {
      console.log('[SessionContext] Animales restaurados desde localStorage:', sessionAnimals.length, sessionAnimals);
    }
  }, []);

  /**
   * Efecto: Forzar recálculo de estadísticas cuando sessionAnimals se carga
   * Esto soluciona el problema de timing donde sessionStats se calcula antes de que sessionAnimals se cargue desde localStorage
   */
  useEffect(() => {
    if (sessionAnimals.length > 0 && isInitializedRef.current) {
      console.log('[SessionContext] Forzando recálculo de stats con', sessionAnimals.length, 'animales');
      // Trigger recalculation by using the stats
      const stats = calculateStats(sessionAnimals);
      console.log('[SessionContext] Stats recalculadas:', stats);
    }
  }, [sessionAnimals.length, isInitializedRef.current]);

  /**
   * Calcula las estadísticas de la sesión actual
   */
  const calculateStats = useCallback((animals: AnimalHistorico[]): SessionStats => {
    const animalesConPeso = animals.filter(a => a.peso && a.peso > 0);
    const pesoTotal = animalesConPeso.reduce((sum, a) => sum + (a.peso || 0), 0);
    const { general: pesoPromedio, macho: peso_promedio_macho, hembra: peso_promedio_hembra, sinSexo: peso_promedio_sin_sexo } = calcularPesoPromedioPorSexo(animals);
    const { macho: cantidad_macho, hembra: cantidad_hembra, sinSexo: cantidad_sin_sexo } = calcularConteoPorSexo(animals);

    const stats = {
      cantidad_animales: animals.length,
      peso_total: pesoTotal,
      peso_promedio: pesoPromedio,
      peso_promedio_macho,
      peso_promedio_hembra,
      peso_promedio_sin_sexo,
      cantidad_macho,
      cantidad_hembra,
      cantidad_sin_sexo,
    };

    console.log('[SessionContext] calculateStats - animales:', animals.length, 'stats:', stats);

    return stats;
  }, []);

  /** Estadísticas calculadas automáticamente */
  const sessionStats = useMemo(() => calculateStats(sessionAnimals), [sessionAnimals, calculateStats]);

  const startNewSession = useCallback((): string => {
    const code = generateSessionCode();
    setSessionCode(code);
    setSessionAnimals([]); // Limpiar animales de la sesión anterior
    return code;
  }, []);

  /**
   * Agrega un animal a la sesión actual.
   * Si ya existe un animal con la misma caravana:
   * - Reemplaza si el fecha_de_evento es más reciente
   * - Si fecha_de_evento es igual, reemplaza con el último agregado (fecha_de_creación)
   */
  const addAnimalToSession = useCallback((animal: AnimalHistorico) => {
    setSessionAnimals(prev => upsertAnimal(prev, animal));
  }, []);

  /**
   * Saca un animal del caché de la sesión (p.ej. tras eliminarDeSesion: el
   * registro histórico quedó anulado en Supabase y no debe seguir apareciendo
   * en el listado ni contar en las estadísticas del form).
   */
  const removeAnimalFromSession = useCallback((caravana: string) => {
    const norm = normalizeCaravana(caravana);
    setSessionAnimals(prev => prev.filter(a => normalizeCaravana(a.caravana) !== norm));
  }, []);

  /**
   * Renombra la caravana de un animal del caché de sesión (tras
   * corregirCaravana). Reutiliza el merge de upsertAnimal: si la caravana
   * correcta ya existía en la sesión, queda un solo registro.
   */
  const renameAnimalInSession = useCallback((caravanaVieja: string, caravanaNueva: string) => {
    const vieja = normalizeCaravana(caravanaVieja);
    const nueva = normalizeCaravana(caravanaNueva);
    if (!vieja || !nueva) return;
    setSessionAnimals(prev => {
      const target = prev.find(a => normalizeCaravana(a.caravana) === vieja);
      if (!target) return prev;
      return upsertAnimal(
        prev.filter(a => a !== target),
        { ...target, caravana: nueva },
      );
    });
  }, []);

  /**
   * Actualiza campos de un animal del caché de sesión (sin tocar el
   * histórico). Sincroniza el caché con las ediciones inline para que el
   * listado en vivo, las stats y el restore tras refresh no muestren
   * valores viejos.
   */
  const updateAnimalInSession = useCallback((caravana: string, campos: Partial<AnimalHistorico>) => {
    const norm = normalizeCaravana(caravana);
    setSessionAnimals(prev => prev.map(a =>
      normalizeCaravana(a.caravana) === norm ? { ...a, ...campos } : a
    ));
  }, []);

  const clearSessionAnimals = useCallback(() => {
    console.log('[SessionContext] Animales de la sesión limpiados');
    setSessionAnimals([]);
    // Limpiar localStorage para sessionAnimals también
    saveSessionState(SESSION_STORAGE_KEYS.SESSION_ANIMALS, null);
  }, []);

  const clearSession = useCallback(() => {
    if (sessionCode) {
      console.log('[SessionContext] Sesión finalizada:', sessionCode);
    }
    setSessionCode(null);
    setSessionAnimals([]);
    setActiveFieldsState(DEFAULT_ACTIVE_FIELDS); // Resetear campos a los valores por defecto
    // Limpiar localStorage al cerrar sesión explícitamente
    clearSessionState();
  }, [sessionCode]);

  // Exponer clearSession al effect de aislamiento por empresa
  clearSessionRef.current = clearSession;

  const setActiveFieldsHandler = useCallback((fields: Set<string>) => {
    setActiveFieldsState(fields);
    // Nota: El useEffect guardará automáticamente en localStorage
  }, []);

  const setLastRazaHandler = useCallback((raza: string | null) => {
    setLastRazaState(raza);
    saveSessionState(SESSION_STORAGE_KEYS.LAST_RAZA, raza);
  }, []);

  const setLastRenspaHandler = useCallback((renspa: string | null) => {
    setLastRenspaState(renspa);
    saveSessionState(SESSION_STORAGE_KEYS.LAST_RENSPA, renspa);
  }, []);

  const setLastCategoriaHandler = useCallback((categoria: string | null) => {
    setLastCategoriaState(categoria);
    saveSessionState(SESSION_STORAGE_KEYS.LAST_CATEGORIA, categoria);
  }, []);

  const setLastUbicacionHandler = useCallback((ubicacion: { id: string; nombre: string } | null) => {
    setLastUbicacionState(ubicacion);
    saveSessionState(SESSION_STORAGE_KEYS.LAST_UBICACION, ubicacion ? JSON.stringify(ubicacion) : null);
  }, []);

  return (
    <SessionContext.Provider
      value={{
        sessionCode,
        sessionStats,
        sessionAnimals,
        lastRaza,
        lastRenspa,
        lastCategoria,
        lastUbicacion,
        startNewSession,
        addAnimalToSession,
        removeAnimalFromSession,
        renameAnimalInSession,
        updateAnimalInSession,
        setLastRaza: setLastRazaHandler,
        setLastRenspa: setLastRenspaHandler,
        setLastCategoria: setLastCategoriaHandler,
        setLastUbicacion: setLastUbicacionHandler,
        clearSessionAnimals,
        clearSession,
        activeFields,
        setActiveFields: setActiveFieldsHandler,
        isActive: sessionCode !== null,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

/**
 * Hook para acceder al contexto de sesión.
 * Debe usarse dentro de un SessionProvider.
 */
export const useSession = (): SessionContextType => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession debe usarse dentro de un SessionProvider');
  }
  return context;
};

export default SessionContext;
